import { useContext, useState, useRef, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import JarFlyerboardContext from '@/context/JarFlyerboardContext';
import { Tag } from '@/design-system';
import { css } from '@emotion/css';
import TuneIcon from '@mui/icons-material/Tune';
import colors from '@/theme/colors';
import { JAR_TAG_FILTERS, JarTagFilter } from '@/context/JarFlyerboardProvider';
import { OwningTypeEnum } from '@/graphql/types';
import { LiveFeedback, ToggleViewControl } from '@/components';
import { useLiveFeedback } from '@/hooks';
import { JarFilter } from './JarFilter';

export const JarFilterButtons: React.FC<{ showContentSwitcher: boolean }> = ({
  showContentSwitcher,
}) => {
  const { drawerFilters, toggleQuickFilter, amountActiveFilters, trackingFrom } =
    useContext(JarFlyerboardContext);

  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [liveMessage, setLiveMessage] = useLiveFeedback({ delay: 2500 });
  const lastToggleYRef = useRef<number>(0);
  const distanceInDirectionRef = useRef<number>(0);
  const lastDirectionRef = useRef<'down' | 'up'>('down');
  const lastToggleAtRef = useRef<number>(0);

  // Asymmetric thresholds to reduce flicker
  const HIDE_DISTANCE_PX = 64; // need more distance to hide
  const SHOW_DISTANCE_PX = 36; // need less distance to show
  const MIN_TOGGLE_INTERVAL_MS = 500; // debounce layout/animation-induced jitter
  const EPSILON = 0.5; // ignore tiny changes

  useEffect(() => {
    const initial = scrollY.get();
    lastToggleYRef.current = initial;
    if (initial <= 0) {
      setHidden(false);
    }
  }, [scrollY]);

  useMotionValueEvent(scrollY, 'change', current => {
    const prev = scrollY.getPrevious() ?? current;
    const diff = current - prev;
    const now = Date.now();

    // Always show at top
    if (current <= 0) {
      if (hidden) {
        setHidden(false);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
      }
      distanceInDirectionRef.current = 0;
      lastDirectionRef.current = 'down';
      return;
    }

    if (Math.abs(diff) < EPSILON) return;

    const direction: 'down' | 'up' = diff > 0 ? 'down' : 'up';

    // Reset accumulation on direction change
    if (direction !== lastDirectionRef.current) {
      distanceInDirectionRef.current = 0;
      lastDirectionRef.current = direction;
    }

    // Accumulate absolute distance while moving in a direction
    distanceInDirectionRef.current += Math.abs(diff);

    // Debounce toggles
    if (now - lastToggleAtRef.current < MIN_TOGGLE_INTERVAL_MS) return;

    if (!hidden && direction === 'down') {
      if (distanceInDirectionRef.current >= HIDE_DISTANCE_PX) {
        setHidden(true);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
        distanceInDirectionRef.current = 0;
      }
    } else if (hidden && direction === 'up') {
      if (distanceInDirectionRef.current >= SHOW_DISTANCE_PX) {
        setHidden(false);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
        distanceInDirectionRef.current = 0;
      }
    }
  });

  const filterOptions: JarTagFilter[] = Object.values(JAR_TAG_FILTERS);

  const onToggle = (option: JarTagFilter, isCurrentlyActive: boolean) => {
    toggleQuickFilter(option);
    const actionText = isCurrentlyActive ? 'removed' : 'applied';
    setLiveMessage(`${option} filter ${actionText}.`);
  };

  const isQuickFilterActive = (tag: JarTagFilter): boolean => {
    if (tag === JAR_TAG_FILTERS.SCHOOL) {
      return drawerFilters.owningType === OwningTypeEnum.district;
    }
    if (tag === JAR_TAG_FILTERS.COMMUNITY) {
      return drawerFilters.owningType === OwningTypeEnum.organization;
    }
    if (tag === JAR_TAG_FILTERS.FREE) {
      return drawerFilters.isCommunityFree === true;
    }
    return false;
  };

  return (
    <>
      <LiveFeedback message={liveMessage} />
      <AnimatePresence initial={false} mode="wait">
        {!hidden && (
          <motion.div
            className={cn.filtersWrapper(showContentSwitcher)}
            key="filters-wrapper"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: 'easeInOut' },
              opacity: { duration: 0.2 },
            }}
            style={{ overflow: 'hidden' }}
          >
            <div className={cn.filters}>
              <JarFilter>
                <Tag
                  rightIcon={<TuneIcon sx={{ fontSize: '15px' }} />}
                  variant={amountActiveFilters ? 'active' : 'outline'}
                  className={cn.filtersTag}
                  nonTranslate
                >
                  {amountActiveFilters > 0 ? (
                    <span key="badge" className={cn.badge}>
                      {amountActiveFilters}
                    </span>
                  ) : null}
                </Tag>
              </JarFilter>
              <fieldset className={cn.radioGroup}>
                <legend className={cn.visuallyHidden}>Filter by type</legend>
                {filterOptions.map(option => {
                  const isActive = isQuickFilterActive(option);
                  return (
                    <label key={option} className={cn.radioLabel}>
                      <input
                        type="radio"
                        name="jar-category-filter"
                        value={option}
                        checked={isActive}
                        onClick={() => {
                          if (isActive) {
                            onToggle(option, isActive);
                          }
                        }}
                        onChange={() => {
                          if (!isActive) {
                            onToggle(option, isActive);
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onToggle(option, isActive);
                          }
                        }}
                        className={cn.radioInput}
                      />
                      <Tag variant={isActive ? 'active' : 'outline'} className={cn.tagOption}>
                        {option}
                      </Tag>
                    </label>
                  );
                })}
              </fieldset>
              <div className={cn.buttonGrid}>
                <ToggleViewControl trackingFrom={trackingFrom} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const cn = {
  filtersWrapper: (withMargin: boolean) => css`
    background: ${colors.white};
    width: 100%;
    z-index: 1198;
    overflow: hidden;
    padding: 2px;
    ${withMargin &&
    css`
      margin-top: 16px;
    `}
  `,
  filters: css`
    display: flex;
    flex-wrap: wrap;
    padding: 0px 16px 16px;
    gap: 8px;
    align-items: center;
  `,
  radioGroup: css`
    display: flex;
    gap: 8px;
    border: none;
    padding: 0;
    margin: 0;
    align-items: center;
    flex-wrap: wrap;
  `,
  radioLabel: css`
    cursor: pointer;
    display: inline-flex;
    outline: none;
  `,
  radioInput: css`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    &:focus-visible + div {
      box-shadow: 0 0 0 2px ${colors.focusOutlineColor};
    }
  `,
  visuallyHidden: css`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `,
  filtersTag: css`
    width: 52px;
    justify-content: center;
  `,
  tagOption: css`
    text-transform: capitalize;
  `,
  badge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.white};
    color: ${colors.jungle};
    border-radius: 50%;
    height: 14px;
    width: 14px;
    text-align: center;
    font-size: 10px;
    line-height: 16px;
    letter-spacing: -0.3px;
  `,
  buttonGrid: css`
    margin-left: auto;
    @media (max-width: 600px) {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
  `,
};
